/**
 * Itens do plano de cuidado (A130): o que entra no "Meu plano de cuidado"
 * da aba Cuidado além do que o motor produz (lembretes e ferramentas
 * recomendadas, A129).
 *
 * Duas origens, as duas pedidas na reunião de sprint de 09/10/2026:
 *  - PROFISSIONAL: a recomendação que a equipe registra no prontuário depois
 *    da consulta ("Consulta com especialista em fertilidade");
 *  - PACIENTE: o que ela mesma quer acompanhar — a jornada muda ao longo do
 *    ciclo, e o plano acompanha.
 *
 * Texto livre de propósito: as ações de cada item (agendar, anexar o exame)
 * dependem do mapeamento que o time clínico ficou de fazer. O estado é só
 * pendente ou feito (`feitoEm`). Apagar é `deletedAt`.
 */
import { z } from 'zod';
import { brandIdField } from './brand';

export const TITULO_DO_ITEM_DO_PLANO_MAX = 80;
export const DESCRICAO_DO_ITEM_DO_PLANO_MAX = 300;
export const LOCAL_DO_ITEM_DO_PLANO_MAX = 120;

/**
 * O que o item é (11/10/2026). Cada tipo tem a própria forma no app — a
 * consulta com profissional, data e horário (e vai para "Minhas consultas"),
 * o exame com data e local, o hábito com a frequência e um ícone. Ausente é o
 * item em texto livre da A130, que continua valendo (e é o que a equipe
 * recomenda pelo prontuário).
 */
export const TipoDoItemDoPlanoEnum = z.enum(['EXAME', 'CONSULTA', 'HABITO']);
export type TipoDoItemDoPlano = z.infer<typeof TipoDoItemDoPlanoEnum>;

/** O ícone que ela escolhe para o hábito. */
export const IconeDoHabitoEnum = z.enum(['AGUA', 'CAMINHADA', 'SONO', 'ALIMENTACAO', 'RESPIRACAO', 'REMEDIO', 'LEITURA', 'OUTRO']);
export type IconeDoHabito = z.infer<typeof IconeDoHabitoEnum>;

const ProfissionalDoItemSchema = z.object({
  nome: z.string().trim().min(1).max(TITULO_DO_ITEM_DO_PLANO_MAX),
  especialidade: z.string().trim().max(TITULO_DO_ITEM_DO_PLANO_MAX).nullable().optional(),
  /** A foto, guardada como mídia privada da paciente (A131): só ela lê. */
  fotoMidiaId: z.string().max(200).nullable().optional(),
});

export const OrigemDoItemDoPlanoEnum = z.enum(['PROFISSIONAL', 'PACIENTE']);
export type OrigemDoItemDoPlano = z.infer<typeof OrigemDoItemDoPlanoEnum>;

export const ItemDoPlanoSchema = z.object({
  id: z.string(),
  /** A paciente — o campo `id` da usuária, não o id do documento. */
  userId: z.string(),
  brandId: brandIdField(),
  origem: OrigemDoItemDoPlanoEnum,
  titulo: z.string().trim().min(1).max(TITULO_DO_ITEM_DO_PLANO_MAX),
  descricao: z.string().trim().max(DESCRICAO_DO_ITEM_DO_PLANO_MAX).nullable().optional(),
  /** Até quando, se houver. Dia, sem hora (`AAAA-MM-DD`). */
  prazo: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
  tipo: TipoDoItemDoPlanoEnum.nullable().optional(),
  /** Data e hora da consulta ou do exame, com o fuso (ISO 8601). */
  quando: z.string().datetime({ offset: true }).nullable().optional(),
  /** Onde: clínica, laboratório, endereço. */
  local: z.string().trim().max(LOCAL_DO_ITEM_DO_PLANO_MAX).nullable().optional(),
  /** Com quem, na consulta. */
  profissional: ProfissionalDoItemSchema.nullable().optional(),
  /** Quantas vezes por semana, no hábito; 7 é todo dia. */
  vezesPorSemana: z.number().int().min(1).max(7).nullable().optional(),
  iconeDoHabito: IconeDoHabitoEnum.nullable().optional(),
  feitoEm: z.coerce.date().nullable().default(null),
  criadoPor: z.string(),
  /** Quem indicou, para a paciente ver ("Indicado por Dra. Mayra"). Só na origem PROFISSIONAL. */
  criadoPorNome: z.string().nullable().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  deletedAt: z.coerce.date().nullable().default(null),
});
export type ItemDoPlano = z.infer<typeof ItemDoPlanoSchema>;

/**
 * O que se manda para criar ou editar: título obrigatório; o resto conforme o
 * tipo. A consulta exige profissional e data com horário — é o que a faz
 * aparecer em "Minhas consultas".
 */
export const DadosDoItemDoPlanoSchema = ItemDoPlanoSchema.pick({
  titulo: true,
  descricao: true,
  prazo: true,
  tipo: true,
  quando: true,
  local: true,
  profissional: true,
  vezesPorSemana: true,
  iconeDoHabito: true,
}).superRefine((d, ctx) => {
  if (d.tipo === 'CONSULTA') {
    if (!d.profissional?.nome) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['profissional', 'nome'], message: 'Com quem é a consulta?' });
    if (!d.quando) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['quando'], message: 'Quando é a consulta?' });
  }
  if (d.tipo === 'HABITO' && !d.vezesPorSemana) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['vezesPorSemana'], message: 'Com que frequência?' });
});
export type DadosDoItemDoPlano = z.infer<typeof DadosDoItemDoPlanoSchema>;
